/**
 * Contrato compartilhado entre o daemon e os clientes (Tauri / browser).
 * Nada aqui depende de Node ou do DOM.
 */

// ---------------------------------------------------------------------------
// Agentes e autonomia
// ---------------------------------------------------------------------------

export const AGENT_KINDS = ['claude', 'codex', 'gemini', 'grok'] as const;
export type AgentKind = (typeof AGENT_KINDS)[number];

/**
 * Nivel de autonomia unificado. Cada agente traduz isso para as suas proprias
 * flags no registry do daemon -- e a abstracao central do produto.
 */
export const AUTONOMY_LEVELS = ['manual', 'plan', 'auto-edit', 'full-auto', 'yolo'] as const;
export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number];

export const AUTONOMY_LABELS: Record<AutonomyLevel, string> = {
  manual: 'Manual',
  plan: 'Plan (somente leitura)',
  'auto-edit': 'Auto-accept edits',
  'full-auto': 'Full-auto',
  yolo: 'YOLO (sem sandbox)',
};

/** Ordem crescente de risco -- usada pela UI para colorir e avisar. */
export const AUTONOMY_RISK: Record<AutonomyLevel, 0 | 1 | 2 | 3> = {
  plan: 0,
  manual: 0,
  'auto-edit': 1,
  'full-auto': 2,
  yolo: 3,
};

export interface AgentAvailability {
  agent: AgentKind;
  label: string;
  installed: boolean;
  version: string | null;
  path: string | null;
  /** Niveis de autonomia que este agente sabe aplicar. */
  supportedAutonomy: AutonomyLevel[];
  /** Se da para alternar autonomia dentro da sessao viva (sem respawn). */
  supportsRuntimeAutonomyToggle: boolean;
  /** Se da para retomar a conversa anterior ao reiniciar a sessao. */
  supportsResume: boolean;
  /** O que este agente permite em termos de retomada de conversa. */
  resume: ResumeCapabilities;
}

// ---------------------------------------------------------------------------
// Perfis de configuracao
// ---------------------------------------------------------------------------

export interface AgentProfile {
  id: string;
  name: string;
  agent: AgentKind;
  /** Alias ('opus', 'sonnet') ou nome completo. null = default do CLI. */
  model: string | null;
  autonomy: AutonomyLevel;
  cwd: string;
  /** Texto acrescentado ao system prompt (suportado por claude e gemini). */
  appendSystemPrompt: string | null;
  /** Allowlist de ferramentas, ex: ['Bash(git *)', 'Edit'] (claude). */
  allowedTools: string[];
  disallowedTools: string[];
  /** Diretorios extras com acesso liberado. */
  addDirs: string[];
  /** Caminho de um arquivo de config MCP a carregar. */
  mcpConfigPath: string | null;
  /** Ignorar MCPs de outras fontes e usar somente o mcpConfigPath. */
  strictMcp: boolean;
  /**
   * Confiar no diretorio sem perguntar. So o Gemini tem flag para isso
   * (`--skip-trust`); claude e codex perguntam dentro do proprio pane.
   */
  skipTrustPrompt: boolean;
  /** Variaveis de ambiente (cifradas em repouso no daemon). */
  env: Record<string, string>;
  /** Escape hatch: argumentos crus anexados ao final do argv. */
  extraArgs: string[];
  createdAt: string;
  updatedAt: string;
}

export type ProfileDraft = Omit<AgentProfile, 'id' | 'createdAt' | 'updatedAt'>;

export function emptyProfileDraft(agent: AgentKind, cwd: string): ProfileDraft {
  return {
    name: '',
    agent,
    model: null,
    autonomy: 'manual',
    cwd,
    appendSystemPrompt: null,
    allowedTools: [],
    disallowedTools: [],
    addDirs: [],
    mcpConfigPath: null,
    strictMcp: false,
    skipTrustPrompt: false,
    env: {},
    extraArgs: [],
  };
}

// ---------------------------------------------------------------------------
// Conversas anteriores (retomada)
// ---------------------------------------------------------------------------

/**
 * Uma conversa ja gravada por um CLI, candidata a ser retomada.
 *
 * Cada agente guarda isso do seu jeito -- o daemon normaliza aqui para a
 * interface nao precisar saber de transcript JSONL nem de indice de thread.
 */
export interface AgentConversation {
  /** Identificador que o proprio CLI aceita no resume. */
  id: string;
  agent: AgentKind;
  /** Titulo legivel, quando o CLI guarda um. */
  title: string | null;
  /** Pasta onde a conversa aconteceu, quando registrada. */
  cwd: string | null;
  gitBranch: string | null;
  updatedAt: string;
  sizeBytes: number;
}

/**
 * Como a sessao deve comecar.
 *
 * - `new`: conversa limpa
 * - `last`: a mais recente daquela pasta
 * - `id`: uma conversa especifica, escolhida na lista
 * - `picker`: deixa o proprio TUI abrir o seletor dele
 */
export const RESUME_MODES = ['new', 'last', 'id', 'picker'] as const;
export type ResumeMode = (typeof RESUME_MODES)[number];

export interface ResumeCapabilities {
  last: boolean;
  byId: boolean;
  picker: boolean;
  /** O daemon consegue listar as conversas deste agente. */
  listable: boolean;
}

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

/**
 * Um workspace e um projeto: uma pasta e as sessoes de agente que trabalham
 * nela. E o agrupamento natural do cockpit -- varias IAs no mesmo repositorio,
 * cada uma no seu pane.
 */
export interface Workspace {
  id: string;
  name: string;
  /** Raiz do projeto. Sessoes criadas no workspace nascem aqui. */
  cwd: string;
  /** Cor de identidade, usada na aba e na borda dos panes. */
  color: string;
  /** Anotacao livre sobre o projeto. */
  notes: string | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export type WorkspaceDraft = Omit<Workspace, 'id' | 'createdAt' | 'updatedAt'>;

/** Cores oferecidas na criacao do workspace. */
export const WORKSPACE_COLORS = [
  '#d97757',
  '#e0a458',
  '#7fb069',
  '#5aa9a3',
  '#7b9bf5',
  '#a97bd4',
  '#d4788f',
  '#9c9488',
] as const;

// ---------------------------------------------------------------------------
// Sessoes
// ---------------------------------------------------------------------------

export const SESSION_STATUSES = ['starting', 'running', 'exited', 'error'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export interface SessionInfo {
  id: string;
  title: string;
  agent: AgentKind;
  workspaceId: string | null;
  profileId: string | null;
  autonomy: AutonomyLevel;
  model: string | null;
  cwd: string;
  status: SessionStatus;
  pid: number | null;
  exitCode: number | null;
  /** argv resolvido -- exposto para a UI mostrar exatamente o que rodou. */
  argv: string[];
  cols: number;
  rows: number;
  /** Numero de clientes assistindo agora. */
  watchers: number;
  createdAt: string;
  lastActivityAt: string;
  errorMessage: string | null;
}

export interface CreateSessionRequest {
  /** Projeto ao qual a sessao pertence; sem cwd explicito, herda o do projeto. */
  workspaceId?: string;
  /** Usa um perfil salvo como base. */
  profileId?: string;
  /** Sobrescreve campos do perfil (ou define tudo, se nao houver perfil). */
  agent?: AgentKind;
  title?: string;
  model?: string | null;
  autonomy?: AutonomyLevel;
  cwd?: string;
  appendSystemPrompt?: string | null;
  skipTrustPrompt?: boolean;
  /** Como iniciar: nova conversa, a ultima, uma especifica, ou o seletor do CLI. */
  resumeMode?: ResumeMode;
  /** Obrigatorio quando resumeMode = 'id'. */
  conversationId?: string;
  env?: Record<string, string>;
  extraArgs?: string[];
  cols?: number;
  rows?: number;
  /** Prompt inicial enviado assim que a sessao subir. */
  initialPrompt?: string;
}

export interface RestartSessionRequest {
  /** Novo nivel de autonomia (o motivo mais comum de respawn). */
  autonomy?: AutonomyLevel;
  model?: string | null;
  /** Retomar a conversa anterior, quando o agente suportar. */
  resume?: boolean;
}

export interface SetAutonomyRequest {
  autonomy: AutonomyLevel;
  /**
   * Se true, o daemon pode reiniciar o processo quando a mudanca nao for
   * aplicavel em runtime. Se false, a chamada falha pedindo confirmacao.
   */
  allowRestart?: boolean;
}

export interface SetAutonomyResult {
  session: SessionInfo;
  /** Como a mudanca foi aplicada. */
  applied: 'runtime' | 'restart' | 'rejected';
  reason?: string;
}

// ---------------------------------------------------------------------------
// Biblioteca de prompts
// ---------------------------------------------------------------------------

export interface PromptTemplate {
  id: string;
  name: string;
  /** Corpo com placeholders {{variavel}}. */
  body: string;
  description: string | null;
  tags: string[];
  /** Nomes das variaveis extraidos do corpo, em ordem de aparicao. */
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

export type PromptDraft = Pick<PromptTemplate, 'name' | 'body' | 'description' | 'tags'>;

/** Extrai {{nomes}} do corpo, sem repetir, na ordem em que aparecem. */
export function extractVariables(body: string): string[] {
  const found: string[] = [];
  const re = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const name = m[1];
    if (!found.includes(name)) found.push(name);
  }
  return found;
}

/** Substitui {{nomes}} pelos valores; placeholders sem valor ficam intactos. */
export function renderPrompt(body: string, vars: Record<string, string>): string {
  return body.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : whole,
  );
}

export interface SendPromptRequest {
  sessionIds: string[];
  /** Texto literal (usado quando nao se envia promptId). */
  text?: string;
  promptId?: string;
  vars?: Record<string, string>;
  /** Anexa Enter ao final, submetendo de fato o prompt. Default: true. */
  submit?: boolean;
}

export interface SendPromptResult {
  delivered: string[];
  failed: { sessionId: string; reason: string }[];
  /** Texto final apos renderizar as variaveis. */
  rendered: string;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface LoginRequest {
  username: string;
  password: string;
}

export interface UserInfo {
  id: string;
  username: string;
  mustChangePassword: boolean;
}

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: UserInfo;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

// ---------------------------------------------------------------------------
// Mensagens WebSocket
// ---------------------------------------------------------------------------

export type ClientMessage =
  | { type: 'attach'; sessionId: string; cols?: number; rows?: number }
  | { type: 'detach'; sessionId: string }
  | { type: 'input'; sessionId: string; data: string }
  | { type: 'resize'; sessionId: string; cols: number; rows: number }
  | { type: 'ping'; at?: number };

export type ServerMessage =
  | { type: 'hello'; daemonVersion: string; user: UserInfo; sessions: SessionInfo[] }
  /** Enviada apos um attach: estado + scrollback para repintar o terminal. */
  | { type: 'attached'; session: SessionInfo; replay: string }
  | { type: 'output'; sessionId: string; data: string }
  | { type: 'session'; session: SessionInfo }
  | { type: 'session-closed'; sessionId: string; exitCode: number | null }
  | { type: 'sessions'; sessions: SessionInfo[] }
  | { type: 'error'; message: string; sessionId?: string }
  | { type: 'pong'; at?: number };

/** Payload de erro padronizado das rotas REST. */
export interface ApiError {
  error: string;
  detail?: string;
}
